// @ts-check
// Peças pequenas das telas dos bots (contrato de bots, seção 22): chip da situação, círculo do bloco com a cor da
// categoria, texto das pendências e dos campos, resumo de um bloco já traduzido, rótulo e tom de cada ramo e a frase de
// cada gatilho. Os componentes maiores reaproveitam as peças dos fluxos (campo, aviso, diálogo, painel).
import { html } from 'htm/preact';
import { Icon } from '../../ui/icon.js';
import { botCategoryStyle } from '../../lib/bot-catalog.js';
import { staticOptionItems } from '../../lib/bot-tree.js';
import { BOT_NODE_CATEGORIES } from '../../../../shared/bots/bot-definition.js';
import { BOT_NODE_ICONS, botSummaryText, botSummaryTitle, summarizeBotNode } from '../../../../shared/bots/bot-summary.js';
import { hasTranslation, t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotNode} BotNode
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotTrigger} BotTrigger
 * @typedef {import('../../../../shared/bots/bot-summary.js').BotSummaryLookups} BotSummaryLookups
 * @typedef {import('../../lib/bot-validation.js').BotEditorProblem} BotEditorProblem
 */

/**
 * Texto de uma pendência: o motivo próprio quando existe (`bots.problemReasons.<CÓDIGO>.<motivo>`), senão o do código.
 * @param {BotEditorProblem} problem
 */
export function botProblemText(problem) {
  const details = /** @type {Record<string, any>} */ (problem.details ?? {});
  const reason = typeof details.reason === 'string' ? details.reason : null;
  if (reason && hasTranslation(`bots.problemReasons.${problem.code}.${reason}`)) return t(`bots.problemReasons.${problem.code}.${reason}`, details);
  if (hasTranslation(`bots.problems.${problem.code}`)) return t(`bots.problems.${problem.code}`, details);
  return t('bots.problems.UNKNOWN');
}

/**
 * Erro de um campo do formulário: "Este campo não pode ficar vazio." ou o texto da pendência que aponta o campo.
 * @param {BotEditorProblem[]} problems @param {string} field
 * @returns {string | null}
 */
export function botFieldProblemText(problems, field) {
  const problem = problems.find((candidate) => candidate.details?.field === field || String(candidate.details?.field ?? '').startsWith(`${field}.`));
  if (!problem) return null;
  if (problem.code === 'NODE_INCOMPLETE' && !problem.details?.reason) return t('bots.panel.required');
  return botProblemText(problem);
}

/**
 * Chip da situação: Rascunho, Ativo, Inativo (com o motivo quando o sistema desativou).
 * @param {{ status: string, inactiveReason?: string | null }} props
 */
export function BotStatusChip({ status, inactiveReason = null }) {
  const reasonText = status === 'INACTIVE' && inactiveReason && inactiveReason !== 'MANUAL' ? t(`bots.inactiveReasons.${inactiveReason}`) : null;
  return html`<span class="flow-status-chip bot-status-chip" data-status=${status} title=${reasonText ?? undefined}>
    ${reasonText ? html`<${Icon} name="IconAlertCircle" size=${12} />` : null}
    <span>${t(`bots.status.${status}`)}</span>
  </span>`;
}

/** Círculo do ícone do bloco com a cor da categoria. @param {{ type: string, size?: number }} props */
export function BotCategoryCircle({ type, size = 32 }) {
  return html`<span class="flow-category-circle" data-size=${size} data-category=${BOT_NODE_CATEGORIES[/** @type {keyof typeof BOT_NODE_CATEGORIES} */ (type)] ?? undefined}
    style=${botCategoryStyle(type)} aria-hidden="true">
    <${Icon} name=${BOT_NODE_ICONS[/** @type {keyof typeof BOT_NODE_ICONS} */ (type)] ?? 'IconHelpCircle'} size=${size >= 32 ? 16 : 14} />
  </span>`;
}

/** Título e resumo de um bloco, traduzidos. @param {BotNode} node @param {BotSummaryLookups} lookups */
export function describeBotNode(node, lookups) {
  const summary = summarizeBotNode(node, lookups);
  return { summary, title: botSummaryTitle(summary, t), text: botSummaryText(summary, t) };
}

const POSITIVE_BRANCHES = new Set(['yes', 'answered', 'identified', 'chosen', 'verified', 'booked', 'success', 'returned']);
const NEGATIVE_BRANCHES = new Set(['no', 'notFound', 'blocked', 'failed', 'unavailable', 'handedOff', 'slotTaken']);
const MUTED_BRANCHES = new Set(['timeout', 'none', 'noSlots']);

/**
 * Tom do rótulo do ramo no canvas (cor do selo): opção, positivo, negativo, outra resposta ou sem resposta.
 * @param {string} branchKey
 * @returns {'option' | 'positive' | 'negative' | 'warning' | 'muted'}
 */
export function botBranchTone(branchKey) {
  if (branchKey === 'invalid') return 'warning';
  if (MUTED_BRANCHES.has(branchKey)) return 'muted';
  if (POSITIVE_BRANCHES.has(branchKey)) return 'positive';
  if (NEGATIVE_BRANCHES.has(branchKey)) return 'negative';
  return 'option';
}

/**
 * Texto de um ramo: o título da opção (ramo de opção estática), a letra com a porcentagem ou o nome do ramo.
 * @param {BotNode | null | undefined} node @param {string} branchKey
 */
export function botBranchLabel(node, branchKey) {
  if (node?.type === 'SEND_MESSAGE') {
    const option = staticOptionItems(node).find((item) => item.id === branchKey);
    if (option) return option.title?.trim() || t('bots.branches.untitledOption');
  }
  if (node?.type === 'SPLIT_PERCENT') {
    const share = node.config?.shares?.[branchKey];
    return t('bots.branches.percent', { letter: branchKey.toUpperCase(), share: typeof share === 'number' ? share : 0 });
  }
  return hasTranslation(`bots.branches.${branchKey}`) ? t(`bots.branches.${branchKey}`) : branchKey;
}

/**
 * Frase de um gatilho ("Primeira mensagem no +55 11 4003-1234").
 * @param {BotTrigger} trigger @param {{ channelLabel: (id: string) => string, teamName: (id: string) => string | null }} lookups
 */
export function describeBotTrigger(trigger, lookups) {
  const channels = (trigger.channelIds ?? []).map((id) => lookups.channelLabel(id)).join(', ');
  const details = [];
  if (trigger.kind === 'KEYWORD') details.push(t('bots.triggers.keywordsValue', { keywords: (trigger.keywords ?? []).map((word) => word.toLowerCase()).join(', ') || '-' }));
  if (trigger.kind === 'STALLED') details.push(t('bots.triggers.stalledValue', { minutes: trigger.minutes ?? '-' }));
  if (trigger.kind === 'TEMPLATE_REPLY') details.push(t('bots.triggers.templateValue', { name: trigger.templateName || '-' }));
  if (trigger.kind === 'OUT_OF_HOURS') details.push(t('bots.triggers.teamValue', { team: (trigger.teamId && lookups.teamName(trigger.teamId)) || '-' }));
  if (trigger.kind === 'TICKET_RESOLVED') {
    const names = (trigger.teamIds ?? []).map((id) => lookups.teamName(id)).filter(Boolean);
    details.push(names.length > 0 ? t('bots.triggers.teamsValue', { teams: names.join(', ') }) : t('bots.triggers.allTeams'));
  }
  return { title: t(`bots.triggers.kinds.${trigger.kind}`), channels, details };
}

/** Selo da finalidade do bot. @param {{ purpose: string }} props */
export function BotPurposeChip({ purpose }) {
  return html`<span class="bot-purpose-chip" data-purpose=${purpose}>${t(`bots.purposes.${purpose}`)}</span>`;
}

/** Texto de um motivo de fim ou de passo, com "Outro motivo" quando não há texto. @param {'END' | 'STEP'} kind @param {string | null | undefined} reason */
export function botReasonText(kind, reason) {
  if (!reason) return t('bots.runs.otherReason');
  const key = kind === 'END' ? `bots.runs.endReasons.${reason}` : `bots.runs.stepReasons.${reason}`;
  return hasTranslation(key) ? t(key) : t('bots.runs.otherReason');
}
